import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Container, Spinner, Alert, Table, Form, Row, Col, Card, Modal, Button, InputGroup } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Event, EventEquipmentItem, QueryParams, EventConsumableItem, EventColor,Category,FullEquipment } from '../interfaces';
import MessageModal from './MessageModal';
import EquipmentDetailModal from './EquipmentDetailModal';
import moment from 'moment';
import _ from 'lodash';
// אייקונים
import { 
  faArrowLeft,
  faEye,
  faCalendarAlt,
  faTimes,
  faCheck,
  faSearch,
  faFilter,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../contexts/AuthContext';

import { API_BASE_URL, buildApiUrl } from '../config/api';

const formatCasesLabel = (quantity: number, caseQty?: number) => {
  const cq = Number(caseQty || 1);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(cq) || cq <= 1) return null;

  const rawCases = quantity / cq;
  const isInt = Number.isInteger(rawCases);
  const casesText = isInt ? String(rawCases) : rawCases.toFixed(2);
  const noun = rawCases === 1 ? 'קייס' : 'קייסים';
  return `${casesText} ${noun}`;
};

const groupEventItems = (items: (EventEquipmentItem | EventConsumableItem)[], sortedCategories: string[]) => {
  const grouped: { [category: string]: { [subcategory: string]: (EventEquipmentItem | EventConsumableItem)[] } } = {};
  items.forEach(item => {
    let category = '';
    let subcategory = '';

    if ('equipment_name' in item) { // EventEquipmentItem
      if (item.is_rented_item) {
        // הציוד המושכר יוצג לפי הקטגוריה והתת-קטגוריה שלו
        category = item.rented_item_category || 'כללי';
        subcategory = item.rented_item_subcategory || 'כללי';
      } else {
        category = item.category_name || 'כללי';
        subcategory = item.subcategory_name || 'כללי';
      }
    } else if ('consumable_name' in item) { // EventConsumableItem
      category = 'מתכלים';
      subcategory = 'כללי';
    } else {
      category = 'אחר';
      subcategory = 'אחר';
    }

    if (!grouped[category]) grouped[category] = {};
    if (!grouped[category][subcategory]) grouped[category][subcategory] = [];
    grouped[category][subcategory].push(item);
  });

  const sortedGrouped: { [category: string]: { [subcategory: string]: (EventEquipmentItem | EventConsumableItem)[] } } = {};
  sortedCategories.forEach(cat => {
    if (grouped[cat]) {
      sortedGrouped[cat] = grouped[cat];
    }
  });
  if (grouped['מתכלים']) {
    sortedGrouped['מתכלים'] = grouped['מתכלים'];
  }

  return sortedGrouped;
};

interface EventPreparationProps {
    onBackToList: () => void;
    onBackToCalendar: () => void;
    onBackToDetails: (eventId: number) => void;
}

const STATUS_LABELS: { [key: string]: string } = {
    'planned': 'מתוכנן',
    'preparation': 'בהכנה',
    'active': 'פעיל/בחוץ',
    'completed': 'הסתיים',
    'cancelled': 'בוטל',
};

const EventPreparation: React.FC<EventPreparationProps> = ({ onBackToList, onBackToCalendar, onBackToDetails }) => {
  const { user } = useAuth();
  const isTechnician = user?.role?.name === 'TECHNICIAN';
  const canViewNoteAuthor = user?.role?.name === 'ADMIN';
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const eventIdNumber = parseInt(eventId || '0', 10);
  
  const [events, setEvents] = useState<Event[]>([]);
  const [eventColors, setEventColors] = useState<EventColor[]>([]);
  const [loadingEvents, setLoadingEvents] = useState<boolean>(true);
  const [errorEvents, setErrorEvents] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<number | ''>(eventIdNumber || '');
  const selectedEvent = useMemo(() => events.find(e => e.id === selectedEventId), [events, selectedEventId]);
  
  const [eventEquipment, setEventEquipment] = useState<EventEquipmentItem[]>([]);
  const [eventConsumables, setEventConsumables] = useState<EventConsumableItem[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [allInventoryEquipment, setAllInventoryEquipment] = useState<FullEquipment[]>([]);
  const [equipmentAvailability, setEquipmentAvailability] = useState<any>(null);

  const [loadingItems, setLoadingItems] = useState<boolean>(false);
  const [errorItems, setErrorItems] = useState<string | null>(null);

  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');
  
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  
  // חיפוש ציוד בהכנת אירועים
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [showSearchFilters, setShowSearchFilters] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterDateRange, setFilterDateRange] = useState<string>('all');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['planned','preparation','active','completed','cancelled','no_equipment_list']);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  
  const [showColorModal, setShowColorModal] = useState<boolean>(false);
  const [selectedColor, setSelectedColor] = useState<number | null>(null);
  const availableColors = useMemo(() => {
    const occupiedColorIds = events.filter(e => e.id !== selectedEventId && e.color).map(e => e.color);
    return eventColors.filter(color => !occupiedColorIds.includes(color.id));
  }, [eventColors, events, selectedEventId]);
  
  // Modal לפרטי ציוד
  const [showEquipmentDetailModal, setShowEquipmentDetailModal] = useState<boolean>(false);
  const [selectedEquipmentForDetails, setSelectedEquipmentForDetails] = useState<FullEquipment | null>(null);
  
  const sortedCategories = useMemo(() => allCategories.sort((a, b) => a.category_name.localeCompare(b.category_name)).map(c => c.category_name), [allCategories]);
  const groupedEventItems = useMemo(() => groupEventItems([...eventEquipment, ...eventConsumables], sortedCategories), [eventEquipment, eventConsumables, sortedCategories]);

  useEffect(() => {
      if (eventIdNumber) {
          setSelectedEventId(eventIdNumber);
      } else {
          setSelectedEventId(''); 
      }
  }, [eventIdNumber]);

  const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
    setMessageModalTitle(title);
    setMessageModalContent(message);
    setMessageModalVariant(variant);
    setShowMessageModal(true);
  };
  
  const fetchEvents = useCallback(async () => {
    setLoadingEvents(true);
    setErrorEvents(null);
    try {
      const params: QueryParams = {};
      if (filterDateRange === 'upcoming_week') {
        const today = moment().startOf('day');
        const nextWeek = moment().add(7, 'days').endOf('day');
        params.start_date__gte = today.toISOString();
        params.start_date__lte = nextWeek.toISOString();
      } else if (filterDateRange === 'custom') {
        if (customStartDate) params.start_date__gte = moment(customStartDate).startOf('day').toISOString();
        if (customEndDate) params.start_date__lte = moment(customEndDate).endOf('day').toISOString();
      }
      if (selectedStatuses.length > 0) {
        params.status__in = selectedStatuses.join(',');
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      const [eventsRes, colorsRes, categoriesRes, equipmentRes] = await Promise.all([
           axios.get(buildApiUrl('events/events/'), { params }),
           axios.get(buildApiUrl('events/event-colors/')),
           axios.get(buildApiUrl('settings/categories/')),
           axios.get(buildApiUrl('inventory/equipment/'))
      ]);
      setEvents(eventsRes.data);
      setEventColors(colorsRes.data);
      setAllCategories(categoriesRes.data);
      setAllInventoryEquipment(equipmentRes.data);
    } catch (err) {
      setErrorEvents("לא ניתן לטעון רשימת אירועים להכנה.");
    } finally {
      setLoadingEvents(false);
    }
  }, [searchQuery, filterDateRange, selectedStatuses, customStartDate, customEndDate]);

  useEffect(() => {
    const handler = setTimeout(() => fetchEvents(), 300);
    return () => clearTimeout(handler);
  }, [fetchEvents]);

  useEffect(() => {
    if (selectedEventId) {
      // onEventSelected(Number(selectedEventId)); // This line is removed as per the new_code
      const fetchEventItems = async () => {
        setLoadingItems(true);
        setErrorItems(null);
        try {
          const [equipmentRes, consumablesRes, availabilityRes] = await Promise.all([
            axios.get(buildApiUrl(`events/event-equipment/?event=${selectedEventId}`)),
            axios.get(buildApiUrl(`events/event-consumables/?event=${selectedEventId}`)),
            axios.get(buildApiUrl(`events/events/${selectedEventId}/get-equipment-availability-status/`))
          ]);
          setEventEquipment(equipmentRes.data);
          setEventConsumables(consumablesRes.data);
          setEquipmentAvailability(availabilityRes.data);
          
          // לא מציגים עוד בחירת צבע אוטומטית בעת בחירת אירוע
          
        } catch (err) {
          setErrorItems("לא ניתן לטעון פריטים לאירוע זה.");
        } finally {
          setLoadingItems(false);
        }
      };
      fetchEventItems();
      // עדכון כתובת ה-URL לקישור ישיר לאירוע שנבחר
      navigate(`/event-preparation/${selectedEventId}`, { replace: true });
    } else {
      setEventEquipment([]);
      setEventConsumables([]);
      // אם בוטלה הבחירה, חזרה לכתובת כללית של הכנת אירועים
      navigate(`/event-preparation`, { replace: true });
    }
  }, [selectedEventId, events]); // Removed onEventSelected from dependency array
  
  const handleColorSelection = async () => {
    if (!selectedEventId || !selectedColor) return;
    try {
        await axios.patch(buildApiUrl(`events/events/${selectedEventId}/`), { color: selectedColor });
        const evName = events.find(e => e.id === selectedEventId)?.event_name || 'האירוע';
        const colorName = eventColors.find(c => c.id === selectedColor!)?.name || '';
        showMessage('הצלחה', `צבע ${evName} עודכן ל-"${colorName}" בהצלחה.`, 'success');
        setShowColorModal(false);
        setEvents(prevEvents => prevEvents.map(e => e.id === selectedEventId ? { ...e, color: selectedColor, color_hex: eventColors.find(c => c.id === selectedColor)?.hex_code, color_name: eventColors.find(c => c.id === selectedColor)?.name } : e));
    } catch (err) {
        showMessage('שגיאה', 'שגיאה בעדכון צבע האירוע.', 'danger');
    }
  };

  const checkDateRestrictions = (statusField: string, value: boolean) => {
    if (!selectedEvent?.start_date) return true;
    
    const now = new Date();
    const eventDate = new Date(selectedEvent.start_date);
    const oneWeekBefore = new Date(eventDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    const twoDaysBefore = new Date(eventDate.getTime() - 2 * 24 * 60 * 60 * 1000);
    
    // בדיקה שלא ניתן להכין ציוד לפני השבוע שלפני התאריך
    if (statusField === 'is_prepared' && value && now < oneWeekBefore) {
      const daysUntilEvent = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      showMessage('שגיאה', `לא ניתן להכין ציוד לפני השבוע שלפני התאריך של האירוע. האירוע מתוכנן ל-${eventDate.toLocaleDateString('he-IL')} (בעוד ${daysUntilEvent} ימים). ניתן להכין ציוד רק החל מ-${oneWeekBefore.toLocaleDateString('he-IL')}.`, 'danger');
      return false;
    }
    
    // בדיקה שלא ניתן להעמיס ציוד לפני היומיים שלפני התאריך
    if (statusField === 'is_loaded' && value && now < twoDaysBefore) {
      const daysUntilEvent = Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      showMessage('שגיאה', `לא ניתן להעמיס ציוד לפני היומיים שלפני התאריך של האירוע. האירוע מתוכנן ל-${eventDate.toLocaleDateString('he-IL')} (בעוד ${daysUntilEvent} ימים). ניתן להעמיס ציוד רק החל מ-${twoDaysBefore.toLocaleDateString('he-IL')}.`, 'danger');
      return false;
    }
    
    return true;
  };

  const canPrepareEquipment = (item: any) => {
    if (!selectedEvent?.start_date) return true;
    
    const now = new Date();
    const eventDate = new Date(selectedEvent.start_date);
    const oneWeekBefore = new Date(eventDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    // אם הפריט כבר מסומן כהוכן, ניתן לבטל את הסימון בכל זמן
    if (item.is_prepared) return true;
    
    // אם הפריט לא מסומן, ניתן לסמן רק אם אנחנו בתוך התקופה המותרת (שבוע לפני התאריך)
    return now >= oneWeekBefore;
  };

  const canLoadEquipment = (item: any) => {
    if (!selectedEvent?.start_date) return true;
    
    const now = new Date();
    const eventDate = new Date(selectedEvent.start_date);
    const twoDaysBefore = new Date(eventDate.getTime() - 2 * 24 * 60 * 60 * 1000);
    
    // אם הפריט כבר מסומן כהועמס, ניתן לבטל את הסימון בכל זמן
    if (item.is_loaded) return true;
    
    // אם הפריט לא מסומן, ניתן לסמן רק אם אנחנו בתוך התקופה המותרת (יומיים לפני התאריך)
    return now >= twoDaysBefore;
  };

  const handleStatusChange = async (item: any, itemType: 'equipment' | 'consumable', statusField: string, value: boolean) => {
    if (!item.id) return;

    // בדיקת הגבלות תאריכים
    if (!checkDateRestrictions(statusField, value)) {
      return;
    }

    const url = itemType === 'equipment'
        ? buildApiUrl(`events/event-equipment/${item.id}/`)
        : buildApiUrl(`events/event-consumables/${item.id}/`);

    try {
      const response = await axios.patch(url, { [statusField]: value });
      let newEventStatus = selectedEvent?.status;
      let didShowStatusMessage = false;

      if (itemType === 'equipment') {
        const updatedEquipment = eventEquipment.map(e => (e.id === item.id ? response.data : e));
        setEventEquipment(updatedEquipment);
        
        const isAnyEquipmentPrepared = updatedEquipment.some(e => e.is_prepared);
        const isAnyEquipmentLoaded = updatedEquipment.some(e => e.is_loaded);
        const isAllEquipmentPrepared = updatedEquipment.every(e => e.is_prepared);
        const areAllConsumablesPrepared = eventConsumables.every(c => c.is_prepared);

        // חישוב כולל לכלל הפריטים
        const anyPreparedTotal = isAnyEquipmentPrepared || eventConsumables.some(c => c.is_prepared);
        const anyLoadedTotal = isAnyEquipmentLoaded || eventConsumables.some(c => c.is_loaded);

        if (selectedEvent?.status === 'planned' && isAnyEquipmentPrepared) {
            newEventStatus = 'preparation';
        }
        // מעבר ל'active' רק כשכל הפריטים הועמסו
        const totalItems = updatedEquipment.length + eventConsumables.length;
        const loadedItems = updatedEquipment.filter(e => e.is_loaded).length + eventConsumables.filter(c => c.is_loaded).length;
        if (selectedEvent?.status === 'preparation' && loadedItems === totalItems && totalItems > 0) {
            newEventStatus = 'active';
        }

        // חזרה אחורה:
        if (selectedEvent?.status === 'preparation' && !anyPreparedTotal && !anyLoadedTotal) {
          newEventStatus = 'planned';
        }
        if (selectedEvent?.status === 'active' && !anyLoadedTotal) {
          newEventStatus = 'preparation';
        }

        // אם כל הציוד חזר – נסמן 'הסתיים'
        const totalEquip = updatedEquipment.length;
        const returnedEquip = updatedEquipment.filter(e => e.is_returned).length;
        if (totalEquip > 0 && returnedEquip === totalEquip) {
          newEventStatus = 'completed';
        }

        // טריגר צבע: אם נכנסנו ל'בהכנה' ואין צבע – בקש לבחור צבע
        if (newEventStatus === 'preparation') {
          const ev = events.find(e => e.id === selectedEventId);
          if (ev && !ev.color) {
            setShowColorModal(true);
          }
        }

      } else { // Consumable
        const updatedConsumables = eventConsumables.map(c => (c.id === item.id ? response.data : c));
        setEventConsumables(updatedConsumables);
        
        // תיקון 7: עדכון מלאי מתכלים בעת סימון "הועמס"
        if (statusField === 'is_loaded' && value) {
            const consumableToUpdate = updatedConsumables.find(c => c.id === item.id);
            if (consumableToUpdate && consumableToUpdate.consumable) {
                try {
                    const res = await axios.get(buildApiUrl(`settings/consumables/${consumableToUpdate.consumable}/`));
                    const currentStock = res.data.current_stock;
                    const newStock = currentStock - consumableToUpdate.quantity_used;
                    await axios.patch(buildApiUrl(`settings/consumables/${consumableToUpdate.consumable}/`), {
                        current_stock: newStock
                    });
                    showMessage('הצלחה', `כמות המתכלה '${consumableToUpdate.consumable_name}' עודכנה במלאי הכללי.`, 'success');
                } catch (err) {
                    console.error("שגיאה בעדכון מלאי מתכלה:", err);
                    showMessage('שגיאה', 'שגיאה בעדכון כמות המתכלה במלאי.', 'danger');
                }
            }
        }
        
        const areAllEquipmentPrepared = eventEquipment.every(e => e.is_prepared);
        const isAnyConsumablePrepared = updatedConsumables.some(c => c.is_prepared);
        const isAnyConsumableLoaded = updatedConsumables.some(c => c.is_loaded);
        const areAllConsumablesPrepared = updatedConsumables.every(c => c.is_prepared);

        // חישוב כולל לכלל הפריטים
        const anyPreparedTotal = isAnyConsumablePrepared || eventEquipment.some(e => e.is_prepared);
        const anyLoadedTotal = isAnyConsumableLoaded || eventEquipment.some(e => e.is_loaded);
        
        if (selectedEvent?.status === 'planned' && isAnyConsumablePrepared) {
            newEventStatus = 'preparation';
        }
        // מעבר ל'active' רק כשכל הפריטים הועמסו
        const totalItems = eventEquipment.length + updatedConsumables.length;
        const loadedItems = eventEquipment.filter(e => e.is_loaded).length + updatedConsumables.filter(c => c.is_loaded).length;
        if (selectedEvent?.status === 'preparation' && loadedItems === totalItems && totalItems > 0) {
            newEventStatus = 'active';
        }

        // טריגר צבע: אם נכנסנו ל'בהכנה' ואין צבע – בקש לבחור צבע
        if (newEventStatus === 'preparation') {
          const ev = events.find(e => e.id === selectedEventId);
          if (ev && !ev.color) {
            setShowColorModal(true);
          }
        }

        // חזרה אחורה:
        if (selectedEvent?.status === 'preparation' && !anyPreparedTotal && !anyLoadedTotal) {
          newEventStatus = 'planned';
        }
        if (selectedEvent?.status === 'active' && !anyLoadedTotal) {
          newEventStatus = 'preparation';
        }

        // אם כל הציוד חזר – נסמן 'הסתיים'
        const totalEquip = eventEquipment.length;
        const returnedEquip = eventEquipment.filter(e => e.is_returned).length;
        if (totalEquip > 0 && returnedEquip === totalEquip) {
          newEventStatus = 'completed';
        }
      }
      
      // בדיקת הגבלות תאריכים לשינוי סטטוס האירוע
      if (newEventStatus && newEventStatus !== selectedEvent?.status && selectedEvent?.start_date) {
        const now = new Date();
        const eventDate = new Date(selectedEvent.start_date);
        const oneWeekBefore = new Date(eventDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        // בדיקה שלא ניתן לשנות סטטוס ל"בהכנה" לפני השבוע שלפני התאריך
        if (newEventStatus === 'preparation' && now < oneWeekBefore) {
          showMessage('שגיאה', 'לא ניתן לשנות סטטוס ל"בהכנה" לפני השבוע שלפני התאריך', 'danger');
          return;
        }
        
        // בדיקה שלא ניתן לסיים אירוע לפני התאריך שלו
        if (newEventStatus === 'completed' && now < eventDate) {
          showMessage('שגיאה', 'לא ניתן לסיים אירוע לפני התאריך שלו. ניתן לסמן כבוטל או למחוק אותו', 'danger');
          return;
        }
      }

      // עדכון סטטוס רק אם הוא באמת השתנה
      if (newEventStatus && newEventStatus !== selectedEvent?.status) {
        if (newEventStatus !== 'preparation') {
          // כשעוברים מ'בהכנה' לכל סטטוס אחר – ננקה צבע (תואם לוגיקת השרת)
          await axios.patch(buildApiUrl(`events/events/${selectedEventId}/`), { color: null });
          setEvents(prevEvents => prevEvents.map(e => e.id === selectedEventId ? { ...e, status: newEventStatus as string, color: null, color_hex: undefined, color_name: undefined } : e));
          showMessage('הצלחה', `סטטוס האירוע "${selectedEvent?.event_name || ''}" עודכן ל "${STATUS_LABELS[newEventStatus]}" והצבע אופס.`, 'success');
          didShowStatusMessage = true;
        } else {
          await axios.patch(buildApiUrl(`events/events/${selectedEventId}/`), { status: newEventStatus });
          setEvents(prevEvents => prevEvents.map(e => e.id === selectedEventId ? { ...e, status: newEventStatus as string } : e));
          showMessage('הצלחה', `סטטוס האירוע "${selectedEvent?.event_name || ''}" עודכן ל "${STATUS_LABELS[newEventStatus]}"`, 'success');
          didShowStatusMessage = true;
        }
      }

      // רענון מהשרת: תמיד נבדוק אם הסטטוס השתנה בשרת ונעדכן בהתאם
      // הוספת דילי קצר כדי לאפשר ללוגיקה בצד השרת (סיגנלים/שמירות נוספות) להסתיים
      const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));
      await sleep(300);
      try {
        const evRes = await axios.get(buildApiUrl(`events/events/${selectedEventId}/`));
        const serverStatus = evRes.data.status;
        const prevStatus = selectedEvent?.status;
        
        // אם הסטטוס השתנה בשרת, נעדכן את ה-state ונציג הודעה
        if (serverStatus && serverStatus !== prevStatus) {
          setEvents(prevEvents => prevEvents.map(e => e.id === selectedEventId ? {
            ...e,
            status: serverStatus as string,
            color: evRes.data.color ?? null,
            color_hex: evRes.data.color_hex,
            color_name: evRes.data.color_name,
          } : e));
          const statusLabel = STATUS_LABELS[serverStatus] || serverStatus;
          const colorClearedNote = serverStatus !== 'preparation' ? ' והצבע אופס.' : '';
          showMessage('הצלחה', `סטטוס האירוע "${selectedEvent?.event_name || ''}" עודכן ל "${statusLabel}"${colorClearedNote}`, 'success');
        }
      } catch (e) {
        console.error("שגיאה ברענון מהשרת:", e);
        // התעלמות מרענון כושל – לא חוסם את הזרימה
      }
    } catch (err) {
      console.error("שגיאה בעדכון סטטוס:", err);
      showMessage('שגיאה', 'שגיאה בעדכון סטטוס.', 'danger');
      fetchEvents();
    }
  };

  const handleReturnStatusChange = async (item: EventEquipmentItem, value: boolean) => {
    if (!item.id) return;
    try {
      const url = buildApiUrl(`events/event-equipment/${item.id}/`);
      await axios.patch(url, { is_returned: value });
      setEventEquipment(prev => prev.map(eq => eq.id === item.id ? { ...eq, is_returned: value } : eq));
      
      // רענון מהשרת: נבדוק אם הסטטוס השתנה בשרת ונעדכן בהתאם
      const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));
      await sleep(300);
      try {
        const evRes = await axios.get(buildApiUrl(`events/events/${selectedEventId}/`));
        const serverStatus = evRes.data.status;
        const prevStatus = selectedEvent?.status;
        
        // אם הסטטוס השתנה בשרת, נעדכן את ה-state ונציג הודעה
        if (serverStatus && serverStatus !== prevStatus) {
          setEvents(prevEvents => prevEvents.map(e => e.id === selectedEventId ? {
            ...e,
            status: serverStatus as string,
            color: evRes.data.color ?? null,
            color_hex: evRes.data.color_hex,
            color_name: evRes.data.color_name,
          } : e));
          const statusLabel = STATUS_LABELS[serverStatus] || serverStatus;
          const colorClearedNote = serverStatus !== 'preparation' ? ' והצבע אופס.' : '';
          showMessage('הצלחה', `סטטוס האירוע "${selectedEvent?.event_name || ''}" עודכן ל "${statusLabel}"${colorClearedNote}`, 'success');
        }
      } catch (e) {
        console.error("שגיאה ברענון מהשרת:", e);
      }
    } catch (err) {
      console.error("שגיאה בעדכון סטטוס חזרה:", err);
      showMessage('שגיאה', 'שגיאה בעדכון סטטוס חזרה.', 'danger');
    }
  };

  const sortedEventItems = useMemo(() => {
    const equipment = [...eventEquipment].map(item => ({...item, type: 'equipment' as const}));
    const consumables = [...eventConsumables].map(item => ({...item, type: 'consumable' as const}));
    
    const allItems: (EventEquipmentItem & {type: 'equipment'} | EventConsumableItem & {type: 'consumable'})[] = [...equipment, ...consumables];

    const sortedCategories = allCategories.sort((a,b) => a.category_name.localeCompare(b.category_name)).map(c => c.category_name);
    return allItems.sort((a, b) => {
        const catA = a.type === 'equipment' ? (a.category_name || a.rented_item_category || 'כללי') : 'מתכלים';
        const catB = b.type === 'equipment' ? (b.category_name || b.rented_item_category || 'כללי') : 'מתכלים';

        const indexA = sortedCategories.indexOf(catA);
        const indexB = sortedCategories.indexOf(catB);

        if (indexA !== indexB) {
            return indexA - indexB;
        }

        const subA = a.type === 'equipment' ? (a.subcategory_name || a.rented_item_subcategory || 'כללי') : 'כללי';
        const subB = b.type === 'equipment' ? (b.subcategory_name || b.rented_item_subcategory || 'כללי') : 'כללי';
        
        if (subA < subB) return -1;
        if (subA > subB) return 1;
        return 0;
    });
  }, [eventEquipment, eventConsumables, allCategories]);
  
  // פונקציה לסינון הציוד לפי חיפוש
  const filteredEventItems = useMemo(() => {
    let filtered = [...eventEquipment, ...eventConsumables];
    
    // סינון לפי טקסט חיפוש
    if (searchTerm) {
      filtered = filtered.filter(item => {
        if ('equipment_name' in item) {
          return (item.equipment_name || item.rented_item_name || '').toLowerCase().includes(searchTerm.toLowerCase());
        } else if ('consumable_name' in item) {
          return (item.consumable_name || '').toLowerCase().includes(searchTerm.toLowerCase());
        }
        return false;
      });
    }
    
    // סינון לפי קטגוריה
    if (selectedCategory) {
      filtered = filtered.filter(item => {
        if ('equipment_name' in item) {
          return (item.category_name || item.rented_item_category || '') === selectedCategory;
        } else if ('consumable_name' in item) {
          return selectedCategory === 'מתכלים';
        }
        return false;
      });
    }
    
    // סינון לפי תת-קטגוריה
    if (selectedSubcategory) {
      filtered = filtered.filter(item => {
        if ('equipment_name' in item) {
          return (item.subcategory_name || item.rented_item_subcategory || '') === selectedSubcategory;
        }
        return false;
      });
    }
    
    return filtered;
  }, [eventEquipment, eventConsumables, searchTerm, selectedCategory, selectedSubcategory]);

  // קבוצת הציוד המסונן
  const filteredGroupedEventItems = useMemo(() => {
    if (!selectedEventId || !filteredEventItems.length) return {};
    return groupEventItems(filteredEventItems, sortedCategories);
  }, [selectedEventId, filteredEventItems, sortedCategories]);

  // קבלת תת-קטגוריות לפי קטגוריה נבחרת
  const availableSubcategories = useMemo(() => {
    if (!selectedCategory) return [];
    
    const subcategories = new Set<string>();
    [...eventEquipment, ...eventConsumables].forEach(item => {
      if ('equipment_name' in item) {
        if (item.category_name === selectedCategory || item.rented_item_category === selectedCategory) {
          subcategories.add(item.subcategory_name || item.rented_item_subcategory || 'כללי');
        }
      }
    });
    
    return Array.from(subcategories).sort();
  }, [eventEquipment, eventConsumables, selectedCategory]);

  // ניקוי סינון
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedSubcategory('');
  };

  const groupedEventItemsDisplay = useMemo(() => groupEventItems(sortedEventItems, sortedCategories), [sortedEventItems, sortedCategories]);

  // פונקציה לטיפול בלחיצה על פריט ציוד
  const handleEquipmentRowClick = (equipmentItem: EventEquipmentItem) => {
    if (equipmentItem.is_rented_item) {
      // עבור ציוד מושכר, נציג הודעה שאין פרטים מלאים
      showMessage('מידע', 'לציוד מושכר אין פרטים מלאים במערכת. פרטי הציוד המושכר מוצגים בטבלה.', 'info');
      return;
    }
    
    // מצא את הציוד המלא במלאי
    const fullEquipment = allInventoryEquipment.find(eq => eq.id === equipmentItem.equipment);
    if (fullEquipment) {
      setSelectedEquipmentForDetails(fullEquipment);
      setShowEquipmentDetailModal(true);
    } else {
      showMessage('שגיאה', 'לא ניתן למצוא פרטי ציוד מלאים.', 'danger');
    }
  };

  if (loadingEvents) return <Spinner animation="border" />;
  if (errorEvents) return <Alert variant="danger">{errorEvents}</Alert>;

  return (
    <Container className="mt-4">
        <div className="app-sticky-page-toolbar mb-4">
            <div className="d-flex justify-content-between mb-2">
                <div>
                    <Button variant="outline-secondary" onClick={onBackToList} className="me-2">
                        <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                        חזרה לרשימת האירועים
                    </Button>
                    {selectedEventId && (
                        <Button variant="outline-secondary" onClick={() => onBackToDetails(Number(selectedEventId))} className="me-2">
                            <FontAwesomeIcon icon={faEye} className="me-1" />
                            חזרה לפרטי האירוע
                        </Button>
                    )}
                    <Button variant="outline-secondary" onClick={onBackToCalendar}>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        חזרה ללוח שנה
                    </Button>
                </div>
            </div>

            <div className="d-flex align-items-center">
                {selectedEvent?.color_hex && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: '15px' }}>
                        <div
                            style={{
                                width: '30px',
                                height: '30px',
                                backgroundColor: selectedEvent.color_hex,
                                border: '1px solid #ccc',
                                borderRadius: '5px',
                                marginBottom: '4px'
                            }}
                            title={selectedEvent.color_name}
                        ></div>
                        {selectedEvent.color_name && <small>{selectedEvent.color_name}</small>}
                    </div>
                )}
                <h2 className="mb-0">הכנה והעמסת ציוד לאירועים</h2>
            </div>
        </div>
      <Form className="mb-4 p-3 border rounded bg-light">
        <Row className="g-3">
          <Col md={4}>
            <Form.Group controlId="searchQuery">
              <Form.Label>חיפוש אירוע</Form.Label>
              <Form.Control type="text" placeholder="חפש לפי שם אירוע, לקוח..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="statusCheckboxes">
              <Form.Label>סטטוס (תיבות סימון)</Form.Label>
              <div className="d-flex gap-3 flex-wrap">
                {Object.entries(STATUS_LABELS).map(([status, label]) => (
                  <Form.Check
                    key={status}
                    type="checkbox"
                    id={`status-${status}`}
                    label={label}
                    checked={selectedStatuses.includes(status)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (checked) {
                        setSelectedStatuses(prev => Array.from(new Set([...prev, status])));
                      } else {
                        setSelectedStatuses(prev => {
                          const next = prev.filter(s => s !== status);
                          // מניעת מצב שאין אף סטטוס מסומן
                          return next.length === 0 ? prev : next;
                        });
                      }
                    }}
                  />
                ))}
              </div>
              <Form.Text>ניתן לבחור אירועים בכל הסטטוסים כולל הסתיים, בוטל וללא רשימת ציוד.</Form.Text>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="filterDateRange">
              <Form.Label>טווח תאריכים</Form.Label>
              <Form.Select value={filterDateRange} onChange={(e) => setFilterDateRange(e.target.value)}>
                <option value="upcoming_week">השבוע הקרוב</option>
                <option value="all">כל האירועים הפעילים</option>
                <option value="custom">מותאם אישית</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        {filterDateRange === 'custom' && (
          <Row className="g-3 mt-1">
            <Col md={4}>
              <Form.Group controlId="customStartDate">
                <Form.Label>מתאריך</Form.Label>
                <Form.Control type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="customEndDate">
                <Form.Label>עד תאריך</Form.Label>
                <Form.Control type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end gap-2">
              <Button variant="secondary" onClick={() => { setCustomStartDate(''); setCustomEndDate(''); }}>נקה תאריכים</Button>
              <Button variant="outline-secondary" onClick={() => setFilterDateRange('all')}>בטל סינון תאריכים</Button>
            </Col>
          </Row>
        )}
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button variant="outline-secondary" onClick={() => {
            setSearchQuery('');
            setFilterDateRange('all');
            setCustomStartDate('');
            setCustomEndDate('');
            setSelectedStatuses(['planned','preparation','active','completed','cancelled','no_equipment_list']);
          }}>
            ביטול כל הסינונים
          </Button>
        </div>
      </Form>

      <Form.Group className="mb-4">
        <Form.Label>בחר אירוע לניהול ציוד</Form.Label>
        <Form.Select value={selectedEventId} onChange={(e) => setSelectedEventId(parseInt(e.target.value) || '')}>
          <option value="">בחר אירוע...</option>
          {events.map(event => (
            <option key={event.id} value={event.id}>
              {event.event_name} ({event.client_name}) - סטטוס: {STATUS_LABELS[event.status] || event.status}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      
      <Modal show={showColorModal} onHide={() => setShowColorModal(false)} centered>
          <Modal.Header closeButton>
              <Modal.Title>בחר צבע לאירוע</Modal.Title>
          </Modal.Header>
          <Modal.Body>
              <Form.Group controlId="eventColorSelect">
                  <Form.Label>בחר צבע מהרשימה:</Form.Label>
                  <Form.Select value={selectedColor || ''} onChange={e => setSelectedColor(parseInt(e.target.value))}>
                      <option value="">בחר צבע...</option>
                      {availableColors.map(color => (
                          <option key={color.id} value={color.id}>
                              {color.name}
                          </option>
                      ))}
                  </Form.Select>
              </Form.Group>
          </Modal.Body>
          <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowColorModal(false)}>
                <FontAwesomeIcon icon={faTimes} className="me-1" />
                ביטול
              </Button>
              <Button variant="primary" onClick={handleColorSelection} disabled={!selectedColor}>
                <FontAwesomeIcon icon={faCheck} className="me-1" />
                אשר
              </Button>
          </Modal.Footer>
      </Modal>

      {selectedEventId && (
        <Card className="mt-4 shadow-sm">
          <Card.Header className="bg-primary text-white">
            פריטים לאירוע: {events.find(e => e.id === selectedEventId)?.event_name}
          </Card.Header>
          <Card.Body>
            {/* התראה על ציוד לא זמין */}
            {equipmentAvailability?.equipment_availability?.has_unavailable && (
              <Alert variant="warning" className="mb-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <strong>אזהרה:</strong> חלק מהציוד לא זמין לתאריכי האירוע!
                <br />
                <small>
                  {equipmentAvailability.equipment_availability.unavailable_count} פריטים לא זמינים מתוך {equipmentAvailability.equipment_availability.total_equipment} פריטי ציוד.
                </small>
                
                <div className="mt-2">
                  <h6>פירוט הציוד הלא זמין:</h6>
                  <div className="list-group">
                    {equipmentAvailability.equipment_availability.unavailable_equipment.map((item: any, index: number) => (
                      <div key={index} className="list-group-item list-group-item-danger">
                        <div className="d-flex w-100 justify-content-between">
                          <h6 className="mb-1">{item.equipment_name}</h6>
                          <small>
                            {item.quantity_needed} יחידות נדרשות
                            {item.unavailable_quantity && (
                              <span className="text-danger ms-2">
                                ({item.unavailable_quantity} לא זמינות)
                              </span>
                            )}
                          </small>
                        </div>
                        <p className="mb-1 text-muted">
                          <strong>סיבה:</strong> {item.availability_reason}
                          {item.available_quantity !== undefined && (
                            <span className="ms-2">
                              <strong>זמין במלאי:</strong> {item.available_quantity} יחידות
                            </span>
                          )}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </Alert>
            )}
            
            {loadingItems && <Spinner animation="border" />}
            {errorItems && <Alert variant="danger">{errorItems}</Alert>}
            {!loadingItems && !errorItems && (
              Object.keys(groupedEventItemsDisplay).length === 0 ? (
                <Alert variant="info">אין פריטים משויכים לאירוע זה עדיין.</Alert>
              ) : (
                <>
                  {/* חיפוש ציוד בהכנת אירועים */}
                  <div className="mb-3 event-preparation-search-container">
                    <InputGroup className="mb-2 event-preparation-search">
                      <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                      <Form.Control
                        className="event-preparation-search-input"
                        placeholder="חפש פריטים..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <Button 
                        className="event-preparation-filter-button"
                        variant="outline-secondary" 
                        onClick={() => setShowSearchFilters(!showSearchFilters)}
                      >
                        <FontAwesomeIcon icon={faFilter} />
                      </Button>
                    </InputGroup>

                    {showSearchFilters && (
                      <div className="mb-3 event-preparation-filters-container">
                        <Row>
                          <Col md={6}>
                            <Form.Label>קטגוריה</Form.Label>
                            <Form.Select 
                              className="event-preparation-category-select"
                              value={selectedCategory} 
                              onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                              <option value="">כל הקטגוריות</option>
                              {sortedCategories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                              <option value="מתכלים">מתכלים</option>
                            </Form.Select>
                          </Col>
                          <Col md={6}>
                            <Form.Label>תת-קטגוריה</Form.Label>
                            <Form.Select 
                              className="event-preparation-subcategory-select"
                              value={selectedSubcategory} 
                              onChange={(e) => setSelectedSubcategory(e.target.value)}
                              disabled={!selectedCategory}
                            >
                              <option value="">כל התת-קטגוריות</option>
                              {availableSubcategories.map(sub => (
                                <option key={sub} value={sub}>{sub}</option>
                              ))}
                            </Form.Select>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2 mt-2">
                          <Button 
                            className="event-preparation-clear-filters-button"
                            variant="secondary" 
                            size="sm" 
                            onClick={clearFilters}
                          >
                            <FontAwesomeIcon icon={faTimes} /> נקה סינונים
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => setShowSearchFilters(false)}>
                            <FontAwesomeIcon icon={faTimes} /> סגור סינונים
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* הודעת תוצאות החיפוש */}
                    {(searchTerm || selectedCategory || selectedSubcategory) && (
                      <Alert variant="info" className="mb-3 event-preparation-search-results-info">
                        <FontAwesomeIcon icon={faSearch} className="me-2" />
                        תוצאות חיפוש: {filteredEventItems.length} פריטים נמצאו
                        {searchTerm && <span className="ms-2">(חיפוש: "{searchTerm}")</span>}
                        {selectedCategory && <span className="ms-2">(קטגוריה: {selectedCategory})</span>}
                        {selectedSubcategory && <span className="ms-2">(תת-קטגוריה: {selectedSubcategory})</span>}
                      </Alert>
                    )}
                  </div>

                  {/* הודעת "אין תוצאות" אם יש סינון פעיל */}
                  {(searchTerm || selectedCategory || selectedSubcategory) && 
                   Object.keys(filteredGroupedEventItems).length === 0 && (
                    <Alert variant="warning">
                      <FontAwesomeIcon icon={faSearch} className="me-2" />
                      לא נמצאו פריטים התואמים לחיפוש שלך. נסה לשנות את תנאי החיפוש.
                    </Alert>
                  )}
                  
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                    <tr>
                      <th>פריט</th>
                      <th style={{width: '100px'}}>כמות</th>
                      <th>קטגוריה</th>
                      <th>תת-קטגוריה</th>
                      {!isTechnician && <th>הערות ציוד</th>}
                      <th>הוכן?</th>
                      <th>הועמס?</th>
                      <th>האם חזר?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries((searchTerm || selectedCategory || selectedSubcategory) ? filteredGroupedEventItems : groupedEventItemsDisplay).map(([category, subcategories]) => (
                      <React.Fragment key={category}>
                                        <tr className="table-dark">
                  <td colSpan={isTechnician ? 7 : 8}><strong>{category}</strong></td>
                </tr>
                        {Object.entries(subcategories).map(([subcategory, items]) => (
                          <React.Fragment key={subcategory}>
                            <tr className="table-secondary">
                              <td colSpan={isTechnician ? 7 : 8} className="ps-4"><strong>{subcategory}</strong></td>
                            </tr>
                            {items.map(item => {
                              if ('equipment_name' in item) { // EventEquipmentItem
                                const inventoryItem = allInventoryEquipment.find(invEq => invEq.id === item.equipment);
                                const isHeavy = inventoryItem?.is_heavy;
                                const casesLabel = formatCasesLabel(item.quantity_needed, inventoryItem?.case_quantity);
                                return (
                                  <React.Fragment key={`eq-${item.id}`}>
                                    <tr className={item.is_rented_item ? 'table-info' : 'table-primary'}>
                                      <td 
                                        onClick={() => !item.is_rented_item ? handleEquipmentRowClick(item) : undefined}
                                        style={{ cursor: !item.is_rented_item ? 'pointer' : 'default' }}
                                        title={!item.is_rented_item ? 'לחץ לצפייה בפרטי הציוד' : undefined}
                                      >
                                        {item.equipment_name || item.rented_item_name}
                                        {isHeavy && <span className="text-danger ms-2" title="ציוד כבד">(כבד)</span>}
                                      </td>
                                      <td>
                                        {item.quantity_needed}
                                        {casesLabel && (
                                          <small className="d-block text-muted">
                                            ({casesLabel})
                                          </small>
                                        )}
                                      </td>
                                      <td>{item.category_name || item.rented_item_category || '-'}</td>
                                      <td>{item.subcategory_name || item.rented_item_subcategory || '-'}</td>
                                      {!isTechnician && (
                                        <td>
                                          {item.notes || '-'}
                                          {canViewNoteAuthor && item.note_updated_by_name && (
                                            <small className="d-block text-muted">נרשם על ידי: {item.note_updated_by_name}</small>
                                          )}
                                        </td>
                                      )}
                                      <td>
                                        <Form.Check 
                                          type="checkbox" 
                                          checked={item.is_prepared} 
                                          onChange={(e) => handleStatusChange(item, 'equipment', 'is_prepared', e.target.checked)}
                                          disabled={!canPrepareEquipment(item)}
                                        />
                                      </td>
                                      <td>
                                        <Form.Check 
                                          type="checkbox" 
                                          checked={item.is_loaded} 
                                          onChange={(e) => handleStatusChange(item, 'equipment', 'is_loaded', e.target.checked)}
                                          disabled={!canLoadEquipment(item)}
                                        />
                                      </td>
                                      <td><Form.Check type="checkbox" checked={item.is_returned} onChange={(e) => handleReturnStatusChange(item, e.target.checked)} /></td>
                                    </tr>
                                    {item.is_rented_item && (
                                      <tr className="table-secondary">
                                        <td colSpan={isTechnician ? 7 : 8}>
                                          <div className="d-flex justify-content-start align-items-center flex-wrap px-4">
                                            <span className="me-4"><strong>ספק:</strong> {item.rental_supplier_name || 'אין'}</span>
                                            <span className="me-4"><strong>תאריך הגעה:</strong> {item.rented_arrival_date || 'אין'}</span>
                                            <span className="me-4"><strong>תאריך עזיבה:</strong> {item.rented_departure_date || 'אין'}</span>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              } else if ('consumable_name' in item) { // EventConsumableItem
                                return (
                                  <tr key={`cn-${item.id}`} className="table-success">
                                    <td>{item.consumable_name}</td>
                                    <td>{item.quantity_used} {item.consumable_unit || ''}</td>
                                    <td>מתכלה</td>
                                    <td>-</td>
                                    {!isTechnician && <td>-</td>}
                                    <td>
                                      <Form.Check 
                                        type="checkbox" 
                                        checked={item.is_prepared} 
                                        onChange={(e) => handleStatusChange(item, 'consumable', 'is_prepared', e.target.checked)}
                                        disabled={!canPrepareEquipment(item)}
                                      />
                                    </td>
                                    <td>
                                      <Form.Check 
                                        type="checkbox" 
                                        checked={item.is_loaded} 
                                        onChange={(e) => handleStatusChange(item, 'consumable', 'is_loaded', e.target.checked)}
                                        disabled={!canLoadEquipment(item)}
                                      />
                                    </td>
                                    <td>-</td>
                                  </tr>
                                );
                              }
                              return null;
                            })}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
                </>
              )
            )}
          </Card.Body>
        </Card>
      )}

      <MessageModal show={showMessageModal} onHide={() => setShowMessageModal(false)} title={messageModalTitle} message={messageModalContent} variant={messageModalVariant} />
      
      {/* מודל פרטי ציוד */}
      <EquipmentDetailModal 
        show={showEquipmentDetailModal} 
        onHide={() => setShowEquipmentDetailModal(false)} 
        equipment={selectedEquipmentForDetails} 
        allEquipment={allInventoryEquipment} 
      />
    </Container>
  );
};

export default EventPreparation;
